import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, CONTACT } from "../data/site";
import { EnquiryDialog } from "./EnquiryDialog";
import { motion, AnimatePresence } from "framer-motion";

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [loc.pathname]);

  const solid = scrolled || loc.pathname !== "/";

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${solid ? "bg-cream/80 backdrop-blur-xl border-b border-emerald/10" : "bg-transparent"}`} data-testid="navbar">
      <div className="max-w-[1600px] mx-auto px-5 lg:px-8 flex items-center justify-between h-20 gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo">
          <span className={`flex items-center justify-center rounded-lg transition-colors duration-500 ${solid ? "" : "bg-white/95 shadow-md px-2 py-1.5"}`}>
            <img src={CONTACT.logo} alt="Kerala Holidays Connect" className="h-14 lg:h-16 w-auto object-contain" />
          </span>
          <span className="relative rounded-full px-5 py-2 shadow-lg shadow-emerald/30 ring-1 ring-gold/40 bg-gradient-to-r from-emerald via-emerald-light to-gold">
            <span className="font-serif font-bold text-2xl lg:text-3xl tracking-tight whitespace-nowrap text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.4)]">
              Kerala <span className="text-gold-light">Holidays</span> Connect
            </span>
          </span>
        </Link>

        <nav className="hidden 2xl:flex items-center gap-4">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-[0.78rem] font-medium whitespace-nowrap hover-underline transition-colors ${loc.pathname === l.to ? "text-gold" : solid ? "text-ink/80 hover:text-emerald" : "text-cream/90 hover:text-white"}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" className={`hidden lg:flex 2xl:hidden items-center gap-2 text-sm font-medium whitespace-nowrap ${solid ? "text-emerald" : "text-cream"}`} data-testid="nav-call">
            <Phone className="w-4 h-4" /> {CONTACT.phone}
          </a>
          <EnquiryDialog trigger={
            <button className="hidden sm:inline-flex whitespace-nowrap bg-gold text-white px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold hover:bg-emerald transition-colors" data-testid="nav-quote-btn">Get Free Quote</button>
          } />
          <button className="2xl:hidden p-1" onClick={() => setOpen(true)} data-testid="nav-menu-open" aria-label="Open menu">
            <Menu className={solid ? "text-emerald" : "text-cream"} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-50 bg-emerald-dark text-cream flex flex-col p-8 2xl:hidden" data-testid="mobile-menu">
            <div className="flex justify-between items-center mb-10">
              <span className="font-serif text-2xl">Menu</span>
              <button onClick={() => setOpen(false)} data-testid="nav-menu-close" aria-label="Close menu"><X /></button>
            </div>
            <nav className="flex flex-col gap-1 overflow-y-auto">
              {NAV_LINKS.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.08 + i * 0.04 }}>
                  <Link to={l.to} className="font-serif text-3xl font-light py-2 block hover:text-gold-light transition-colors" data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>{l.label}</Link>
                </motion.div>
              ))}
            </nav>
            <a href={`tel:${CONTACT.phoneRaw}`} className="mt-auto flex items-center gap-2 text-gold-light"><Phone className="w-4 h-4" /> {CONTACT.phone}</a>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
